import { generateRequest } from './request.js';
import { AveRequestEvents } from '../events.js';
/**
 * Set the Camera mode.
 *
 * Example:
 * ```
 * [{
 *   "meta": {
 *     "name": "AVESetCameraMode"
 *   },
 *   "data": {
 *     "CameraMode": "FreeOrbit"
 *   }
 * }]
 * ```
 */
export function generateSetCameraModeRequest({ cameraMode, messageId }) {
    return generateRequest({
        messageId,
        name: AveRequestEvents.AVESetCameraMode,
        data: {
            CameraMode: cameraMode
        }
    });
}
/**
 * Move the camera by the specified delta.
 *
 * Limitations:
 * - Only works while no Camera Transition is active.
 * - Only works if camera has defined degrees of freedom.
 *
 * Notes:
 * - This command is equivalent to the HTTP endpoint HTTP Camera Navigation (cameraDelta).
 * - To have a smooth behavior, while active user input is happening these values should be sent at least 30 times a second.
 *
 * Example:
 * ```
 * [{
 *   "meta": {
 *     "name": "AVECameraDelta",
 *     "type": "REQUEST"
 *   },
 *   "data": {
 *     "pitch": 10,
 *     "yaw": 10,
 *     "zoom": 10,
 *     "PanX": 1,
 *     "PanY": 1,
 *     "FocalLength": 0,
 *     "Distance": 0
 *   }
 * }]
 * ```
 */
export function generateCameraDeltaRequest({ pitch, yaw, zoom, panX, panY, focalLength, distance, messageId }) {
    return generateRequest({
        messageId,
        name: AveRequestEvents.AVECameraDelta,
        data: {
            pitch,
            yaw,
            zoom,
            PanX: panX,
            PanY: panY,
            FocalLength: focalLength,
            Distance: distance
        }
    });
}
/**
 * List all available Cameras. This should be called after Car is loaded.
 *
 * Limitations:
 * - Cameras are only available while a Car is loaded.
 *
 * Notes:
 * - Deprecated for VR. Please use AVESelectVRSpot instead.
 *
 * Example:
 * ```
 * [{
 *   "meta": {
 *     "name": "AVEListCameras",
 *     "type": "REQUEST",
 *     "lastUpdate": true
 *   },
 *   "data": {}
 * }]
 * ```
 */
export function generateListCamerasRequest({ messageId } = {}) {
    return generateRequest({ messageId, name: AveRequestEvents.AVEListCameras });
}
/**
 * Start a Camera transition with a given transition/movement behavior.
 *
 * Limitations:
 * - Requested Camera must be loaded/available.
 *
 * Notes:
 * - An AVESelectCamera can abort/override a running Camera transition.
 * - A "flysmooth" will automatically be replaced with an "Fade" if:
 *   - Camera has the tag "Interior".
 *   - Current camera and requested camera have different location Tags ( "Exterior" / "Interior" ).
 * - AVE 21.16 Will also activate the Standard CameraMode if not active.
 * - Deprecated for VR interaction, please use AVESelectVRSpot.
 *
 * Possible errors:
 *  - Missing_Resource_Camera
 *  - Missing_Resource
 *
 * Example:
 * ```
 * [{
 *   "meta": {
 *     "name": "AVESelectCamera",
 *     "type": "REQUEST"
 *   },
 *   "data": {
 *     "carId": <int>,
 *     "cameraId": <string>,
 *     "transition": {
 *       "movementMode": <String>
 *     }
 *   }
 * }]
 * ```
 */
export function generateSelectCameraRequest({ carId, cameraId, transition, messageId }) {
    return generateRequest({ messageId, name: AveRequestEvents.AVESelectCamera, data: { carId, cameraId, transition } });
}
/**
 * Reset the current Camera to its initial position / settings.
 *
 * Limitations:
 * - Only implemented for FreeOrbitCamera.
 *
 * Example:
 * ```
 * [{
 *   "meta": {
 *     "name": "AVEResetCamera",
 *     "type": "REQUEST"
 *   },
 *   "data": {}
 * }]
 * ```
 */
export function generateResetCameraRequest({ messageId } = {}) {
    return generateRequest({ messageId, name: AveRequestEvents.AVEResetCamera });
}
/**
 * Set a new Position for the Orbit Focus Point by Raycasting to the Geometry.
 *
 * Limitations:
 * - Only implemented for FreeOrbitCamera.
 *
 * Example:
 * ```
 * [{
 *   "meta": {
 *     "name": "AVESetCameraFocusPoint",
 *     "type": "REQUEST"
 *   },
 *   "data": {
 *     "x": 2,
 *     "y": 5
 *   }
 * }]
 * ```
 */
export function generateSetCameraFocusPointRequest({ x, y, messageId }) {
    return generateRequest({ messageId, name: AveRequestEvents.AVESetCameraFocusPoint, data: { x, y } });
}
/**
 * Retrieve the current camera definition.
 *
 * Notes:
 * - Usable in any camera mode, e.g., FreeOrbit-Mode or Standard-Mode; see AVESetCameraMode.
 *
 * Example:
 * ```
 * [{
 *   "meta": {
 *     "name": "AVEGetCurrentCameraDefinition",
 *     "type": "REQUEST",
 *   },
 *   "data": {}
 * }]
 * ```
 */
export function generateGetCurrentCameraDefinitionRequest({ messageId } = {}) {
    return generateRequest({ messageId, name: AveRequestEvents.AVEGetCurrentCameraDefinition });
}
/**
 * Set the current camera to the supplied camera definition.
 *
 * Notes: Automatically changes the Camera Mode to FreeOrbit (see AVESetCameraMode).
 *
 * Example:
 * ```
 * [{
 *   "meta": {
 *     "name": "AVESetCameraDefinition",
 *     "type": "REQUEST",
 *     "source": "http",
 *     "messageId": "<...>",
 *     "lastUpdate": true
 *   },
 *   "data": {
 *     "location": "Ext",
 *     "actorTransform": {
 *       "rotation": {
 *         "x": 0,
 *         "y": 0,
 *         "z": 0,
 *         "w": 1
 *       },
 *       "translation": {
 *         "x": 0,
 *         "y": 0,
 *         "z": 0
 *       },
 *       "scale3D": {
 *         "x": 1,
 *         "y": 1,
 *         "z": 1
 *       }
 *     },
 *     "position": {
 *       "x": 176.02314758300781,
 *       "y": -101.57102966308594,
 *       "z": 42.389240264892578
 *     },
 *     "rotation": {
 *       "pitch": -11.994993209838867,
 *       "yaw": -133.55975341796875,
 *       "roll": 0
 *     },
 *     "distance": 824.90399169921875,
 *     "zoom": 24.5,
 *     "foV": 72.608993530273438
 *   }
 * }]
 * ```
 */
export function generateSetCameraDefinitionRequest({ cameraDefinition, messageId }) {
    return generateRequest({ messageId, name: AveRequestEvents.AVESetCameraDefinition, data: cameraDefinition });
}
//# sourceMappingURL=camera-interaction.js.map